import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { BUCKET, BUCKET_META, SLA_LATE } from '@/pages/RoStatusDashboard/buckets'

import { GUIDELINE_HOURS } from './queue'

/**
 * The queue tiles: one per Work Queue, with its count and the flag that says how urgent
 * it is. The all-dealers dashboard showed them first (WARP-960); the Work Queue uses the
 * same tiles in place of its tabs (WARP-965, Mark: "I'd rather see those big tiles on
 * the work screen"), so the two pages read as one.
 */

// The amber for "past the 48-hour guideline" or "about to age out": a warning, not the
// contract's red (2026-10-01).
export const GUIDELINE = { swatch: '#ef9f27', fill: '#faeeda', ink: '#633806' }

/**
 * A queue's color: the bucket swatch, or gold and blue for the two reviews (Michelle:
 * "gold's for us").
 */
export const swatchFor = (queue) => {
  if (queue.bucket) return BUCKET_META[queue.bucket].swatch
  return BUCKET_META[queue.tone === 'gold' ? BUCKET.OUTSTANDING_ADMIN : BUCKET.OUTSTANDING_DEALER]
    .swatch
}

/** The queue's name without the "OL - " the tabs needed to tell the reviews apart. */
export const shortLabel = (queue) => queue.label.replace(/^OL - /, '')

/** Whole dollars, as the dashboard shows them: "$153,394". */
export const formatDollars = (value) =>
  `$${Math.round(Number(value) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

/**
 * The flag on a queue's count, worst first: late (Ready, Closed), aged out, or past the
 * 48-hour guideline (the reviews).
 */
export const flagFor = (queue, counts) => {
  if (!counts) return null
  if (counts.late) return { value: counts.late, label: 'late', tone: SLA_LATE }
  if (counts.agedOut) return { value: counts.agedOut, label: 'aged out', tone: SLA_LATE }
  if (counts.waiting) {
    const label =
      queue.waitingOn === 'dealer'
        ? `no response ${GUIDELINE_HOURS} h`
        : `over ${GUIDELINE_HOURS} h`
    return { value: counts.waiting, label, tone: GUIDELINE }
  }
  return null
}

/**
 * What a tile says when nothing on it is flagged. Pending OEM says nothing: it has no
 * deadline to be on track for, and its name already says what it waits on. Dealer review
 * is shown but not counted.
 */
export const idleNoteFor = (queue) => {
  if (queue.kind === 'pending') return ''
  return queue.group === 'work' ? 'On track' : 'Not counted as work'
}

export const Swatch = ({ color }) => (
  <i
    aria-hidden
    className="mr-2 mt-px inline-block h-3.5 w-1 shrink-0 rounded-full"
    style={{ backgroundColor: color }}
  />
)

Swatch.propTypes = { color: PropTypes.string.isRequired }

export const Flag = ({ flag, compact }) => (
  <span
    className="inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums tracking-normal"
    style={{ backgroundColor: flag.tone.fill, color: flag.tone.ink }}
    title={`${flag.value} ${flag.label}`}
  >
    {compact ? flag.value : `${flag.value} ${flag.label}`}
  </span>
)

Flag.propTypes = {
  compact: PropTypes.bool,
  flag: PropTypes.shape({
    label: PropTypes.string,
    tone: PropTypes.object,
    value: PropTypes.number,
  }).isRequired,
}

Flag.defaultProps = { compact: false }

// The tiles' shell, shared by the queue tiles and the dashboard's Urgency cards so they
// read as one row. In a `KpiRow` on wide screens, a tile's three parts (label, count,
// flag) take the row's three lines, so the counts line up when a narrow tile wraps its
// label. On small screens, the label takes the spare height for the same reason.
export const KPI_CARD =
  'flex min-w-[7.5rem] flex-1 basis-0 flex-col gap-1 rounded-2xl bg-white p-3 shadow-sm lg:row-span-3 lg:row-start-2 lg:grid lg:grid-rows-subgrid'
export const KPI_LINK = 'hover:bg-charcoal-50 transition-colors'

// One tile's column: no narrower than the minimum, which lets the dashboard's nine tiles
// fit on one line at 1280px, and an equal share of the spare width.
const TILE_TRACK = 'minmax(7.5rem, 1fr)'

/**
 * The row of `KpiGroup`s the tiles sit in.
 *
 * On wide screens, it is a grid with one column per tile, so every tile has the same
 * width and all tiles are on one line. An empty column between two groups doubles the
 * gap there. When the tiles are at their minimum width and still do not fit, the row
 * scrolls sideways. The padding keeps the scroll box from clipping the tiles' shadows
 * and focus rings, and `relative` keeps the tiles' screen-reader text inside it.
 *
 * On small screens, each group gets its own line and its tiles wrap.
 */
export const KpiRow = ({ children, ...props }) => {
  const groups = React.Children.toArray(children).filter(React.isValidElement)
  let column = 1
  const placed = groups.map((group) => {
    const start = column
    column += group.props.tiles + 1
    return React.cloneElement(group, { column: start })
  })

  return (
    <div
      className="relative flex flex-wrap gap-x-4 gap-y-3 lg:-m-1 lg:grid lg:grid-rows-[repeat(4,auto)] lg:gap-x-3 lg:gap-y-1 lg:overflow-x-auto lg:p-1"
      style={{
        gridTemplateColumns: groups
          .map((group) => `repeat(${group.props.tiles}, ${TILE_TRACK})`)
          .join(' 0px '),
      }}
      {...props}
    >
      {placed}
    </div>
  )
}

KpiRow.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * One labeled group of tiles in a `KpiRow`. On wide screens, the group spans one column
 * per tile, and its label and tiles use the row's lines.
 */
export const KpiGroup = ({ children, column, label, tiles }) => (
  <div
    className="flex basis-full flex-col gap-1 lg:row-span-4 lg:grid lg:grid-cols-subgrid lg:grid-rows-subgrid lg:gap-x-3"
    style={{ gridColumn: `${column} / span ${tiles}` }}
  >
    <span className="text-charcoal-500 px-1 text-[10px] font-bold uppercase tracking-widest lg:col-span-full lg:self-end">
      {label}
    </span>
    <div className="flex flex-wrap gap-3 lg:contents">{children}</div>
  </div>
)

KpiGroup.propTypes = {
  children: PropTypes.node.isRequired,
  // The group's first column. `KpiRow` sets it.
  column: PropTypes.number,
  label: PropTypes.string.isRequired,
  // How many tiles the group holds.
  tiles: PropTypes.number.isRequired,
}

KpiGroup.defaultProps = { column: 1 }

/**
 * One queue's tile. A link with `to` (the dashboard opens the queue), or a button with
 * `onClick` (the Work Queue picks it, and `selected` marks the one on screen).
 */
export const QueueTile = ({
  counts,
  idleNote,
  loading,
  onClick,
  queue,
  selected,
  showFlag,
  to,
}) => {
  const flag = showFlag ? flagFor(queue, counts) : null
  const body = (
    <>
      <span className="text-charcoal-700 flex flex-1 items-start text-[11px] font-bold uppercase tracking-widest">
        <Swatch color={swatchFor(queue)} />
        {shortLabel(queue)}
      </span>
      <span className="text-charcoal-900 text-2xl font-semibold tabular-nums">
        {loading ? '—' : (counts?.total || 0).toLocaleString()}
      </span>
      <span className="min-h-[1.125rem]">
        {flag ? (
          <Flag flag={flag} />
        ) : (
          <span className="text-charcoal-400 text-[11px]">{idleNote}</span>
        )}
      </span>
    </>
  )

  if (to) {
    return (
      <Link className={mergeClassNames(KPI_CARD, KPI_LINK)} to={to}>
        {body}
      </Link>
    )
  }
  return (
    <button
      aria-pressed={selected}
      className={mergeClassNames(
        KPI_CARD,
        KPI_LINK,
        'text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800',
        selected && 'ring-2 ring-blue-800',
      )}
      onClick={onClick}
      type="button"
    >
      {body}
    </button>
  )
}

QueueTile.propTypes = {
  // The queue's counts from the counts endpoint, camelCased.
  counts: PropTypes.object,
  // What the tile says when nothing is flagged.
  idleNote: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  queue: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  // The late and 48-hour flags. WPC's signals; see the page for who sees them.
  showFlag: PropTypes.bool,
  to: PropTypes.string,
}

QueueTile.defaultProps = {
  counts: null,
  idleNote: '',
  loading: false,
  onClick: null,
  selected: false,
  showFlag: true,
  to: null,
}
